import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, AudioLines } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
    { to: "/", label: "Home", testid: "nav-link-home" },
    { to: "/music", label: "Music", testid: "nav-link-music" },
    { to: "/videos", label: "Videos", testid: "nav-link-videos" },
    { to: "/about", label: "About", testid: "nav-link-about" },
    { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            style={{ top: "var(--bar-h, 0px)" }}
            className={`fixed inset-x-0 z-50 transition-all duration-300 border-b ${
                scrolled
                    ? "backdrop-blur-xl bg-black/75 border-amber-500/15"
                    : "backdrop-blur-sm bg-black/25 border-transparent"
            }`}
        >
            <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-[72px] flex items-center justify-between">
                <Link to="/" data-testid="nav-brand-logo" className="flex items-center group">
                    <Logo
                        variant="full"
                        size={52}
                        className="transition-transform duration-300 group-hover:scale-[1.04]"
                    />
                </Link>

                <div className="hidden lg:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `relative font-meta text-[11px] tracking-[0.25em] uppercase transition-colors duration-200 py-2 ${
                                    isActive ? "text-amber-300" : "text-zinc-400 hover:text-white"
                                } after:absolute after:left-0 after:bottom-0 after:h-px after:bg-amber-400 after:transition-all after:duration-300 ${
                                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/music"
                        data-testid="nav-stream-now-button"
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-600 text-black font-bold text-xs tracking-wider uppercase px-5 py-2.5 rounded-full hover:shadow-[0_0_25px_rgba(200,151,43,0.4)] hover:scale-105 transition-all duration-200"
                    >
                        <AudioLines size={14} />
                        Listen
                    </Link>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden text-amber-300 p-2"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu-panel"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden backdrop-blur-xl bg-black/90 border-t border-amber-500/15"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`mobile-${l.testid}`}
                                        className={({ isActive }) =>
                                            `block py-3 font-display text-2xl font-bold tracking-tight border-b border-white/5 ${
                                                isActive ? "text-gold-gradient" : "text-white"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
